import * as i1 from '@angular/cdk/dialog';
import { CdkDialogContainer, Dialog, DialogModule } from '@angular/cdk/dialog';
import { OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { Directive, inject, HostBinding, ChangeDetectionStrategy, Component, InjectionToken, Injectable, HostListener, Input, NgModule } from '@angular/core';
import * as i3 from '@tc/interstellar-icon';
import { StlrIconModule } from '@tc/interstellar-icon';
import { generateUUID } from '@tc/foundation/utilities';
import { Subject, merge, filter, takeUntil } from 'rxjs';
import { hasModifierKey } from '@angular/cdk/keycodes';
import * as i2 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i4 from '@tc/interstellar-button';
import { StlrButtonModule } from '@tc/interstellar-button';
import * as i5 from '@tc/interstellar-flex-layout';
import { StlrFlexLayoutModule } from '@tc/interstellar-flex-layout';
import { CommonModule } from '@angular/common';

/**
 * Configuration objects for the StlrDialogService
 */
function StlrDialogContainerComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "h4", 3);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.config.headerText);
  }
}
function StlrDialogContainerComponent_ng_template_6_Template(rf, ctx) {}
function StlrDialogContainerComponent_Conditional_7_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 8);
    i0.ɵɵlistener("click", function StlrDialogContainerComponent_Conditional_7_For_2_Template_button_click_0_listener() {
      const action_r3 = i0.ɵɵrestoreView(_r2).$implicit;
      const ctx_r0 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(action_r3.action(ctx_r0.dialogRef));
    });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const action_r3 = ctx.$implicit;
    i0.ɵɵproperty("type", action_r3.buttonConfig.type || "button")("btnSize", action_r3.buttonConfig.size)("btnType", action_r3.buttonConfig.buttonType)("btnWidth", action_r3.buttonConfig.width)("icon", action_r3.buttonConfig.icon);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", action_r3.label, " ");
  }
}
function StlrDialogContainerComponent_Conditional_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "footer", 6);
    i0.ɵɵrepeaterCreate(1, StlrDialogContainerComponent_Conditional_7_For_2_Template, 2, 6, "button", 7, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵrepeater(ctx_r0.config.actions);
  }
}
class StlrDialogConfig {
  constructor(config) {
    this.actions = [];
    /**
     * If true, disallow closing via backdrop click or Escape key (forces usage of stlr-dialog-close directive)
     */
    this.disableClose = false;
    /**
     * The role this dialog fulfills. Defaults to 'dialog'.
     */
    this.role = 'dialog';
    /**
     * The width of the dialog to display
     */
    this.width = 'med';
    if (config) {
      this.actions = config.actions;
      this.ariaLabel = config.ariaLabel;
      this.ariaLabeledBy = config.ariaLabeledBy;
      this.ariaDescribedBy = config.ariaDescribedBy;
      this.headerText = config.headerText;
      this.id = config.id;
      this.disableClose = config.disableClose;
      this.data = config.data;
      this.role = config.role;
      this.width = config.width;
    }
  }
}
class StlrDialogRef {
  get componentInstance() {
    return this.dialogRef.componentInstance;
  }
  get config() {
    return this.dialogRef.config;
  }
  // ES Lint being dumb (v8.48)
  constructor(dialogRef) {
    this.dialogRef = dialogRef;
    this.id = this.dialogRef.id || `stlr-dialog-${generateUUID(4)}`;
    this._destroyed$ = new Subject();
    /**
     * Set up a pair of subscriptions:
     * 1. The click of the dialogRef's backdrop
     * 2. The Escape key (with NO access modifiers)
     *
     * If either fires, close the dialog (unless disabled by configuration)
     */
    merge(this.dialogRef.backdropClick, this.dialogRef.keydownEvents.pipe(filter(event => event.key === 'Escape' && !hasModifierKey(event)))).pipe(takeUntil(this._destroyed$)).subscribe(event => {
      if (!this.dialogRef.config.disableClose) {
        event.preventDefault();
        this.dialogRef.close();
      }
    });
  }
  afterClosed() {
    return this.dialogRef.closed;
  }
  close(dialogResult) {
    this.dialogRef.close(dialogResult);
  }
  static {
    this.ɵfac = function StlrDialogRef_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrDialogRef)(i0.ɵɵdirectiveInject(i1.DialogRef));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: StlrDialogRef
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrDialogRef, [{
    type: Directive
  }], () => [{
    type: i1.DialogRef
  }], null);
})();
class StlrDialogContainerComponent extends CdkDialogContainer {
  constructor() {
    super(...arguments);
    this.config = inject(StlrDialogConfig);
    this.overlayRef = inject(OverlayRef);
    this.dialogRef = inject(StlrDialogRef);
    this.id = this.config.id;
  }
  ngOnInit() {
    if (this.config.width) {
      this.overlayRef.addPanelClass(this.config.width);
    }
    this.overlayRef.addPanelClass('stlr-dialog-pane');
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵStlrDialogContainerComponent_BaseFactory;
      return function StlrDialogContainerComponent_Factory(__ngFactoryType__) {
        return (ɵStlrDialogContainerComponent_BaseFactory || (ɵStlrDialogContainerComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrDialogContainerComponent)))(__ngFactoryType__ || StlrDialogContainerComponent);
      };
    })();
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: StlrDialogContainerComponent,
      selectors: [["stlr-dialog-container"]],
      hostVars: 1,
      hostBindings: function StlrDialogContainerComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵdomProperty("id", ctx.id);
        }
      },
      standalone: false,
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 8,
      vars: 9,
      consts: [["cdkTrapFocus", "", "stlrLayout", "column"], ["aria-label", "Close", 1, "close-icon", 3, "click"], ["size", "sml"], [2, "margin-bottom", "var(--space-m)"], [1, "outlet"], ["cdkPortalOutlet", ""], ["stlrLayout", "row", "stlrLayoutAlign", "end center", "stlrLayoutGap", "var(--space-s)", 2, "margin-top", "var(--space-m)"], ["stlrButton", "", 3, "type", "btnSize", "btnType", "btnWidth", "icon"], ["stlrButton", "", 3, "click", "type", "btnSize", "btnType", "btnWidth", "icon"]],
      template: function StlrDialogContainerComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0)(1, "button", 1);
          i0.ɵɵlistener("click", function StlrDialogContainerComponent_Template_button_click_1_listener() {
            return ctx.dialogRef.close();
          });
          i0.ɵɵelementStart(2, "stlr-icon", 2);
          i0.ɵɵtext(3, " close ");
          i0.ɵɵelementEnd()();
          i0.ɵɵconditionalCreate(4, StlrDialogContainerComponent_Conditional_4_Template, 2, 1, "h4", 3);
          i0.ɵɵelementStart(5, "div", 4);
          i0.ɵɵtemplate(6, StlrDialogContainerComponent_ng_template_6_Template, 0, 0, "ng-template", 5);
          i0.ɵɵelementEnd();
          i0.ɵɵconditionalCreate(7, StlrDialogContainerComponent_Conditional_7_Template, 3, 0, "footer", 6);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵclassMap(i0.ɵɵinterpolate1("stlr-dialog-container ", ctx.config.width));
          i0.ɵɵattribute("aria-label", ctx.config.ariaLabel)("aria-labelledby", ctx.config.ariaLabeledBy)("aria-describedby", ctx.config.ariaDescribedBy)("role", ctx.config.role);
          i0.ɵɵadvance(4);
          i0.ɵɵconditional(ctx.config.headerText ? 4 : -1);
          i0.ɵɵadvance(3);
          i0.ɵɵconditional(((ctx.config == null ? null : ctx.config.actions == null ? null : ctx.config.actions.length) ?? 0) > 0 ? 7 : -1);
        }
      },
      dependencies: [i1.ɵɵCdkPortalOutlet, i2.CdkTrapFocus, i3.StlrIconComponent, i4.StlrButtonComponent, i5.StlrLayoutDirective, i5.StlrLayoutAlignDirective, i5.StlrLayoutGapDirective],
      styles: [".cdk-global-overlay-wrapper .stlr-dialog-pane{--dialog__margins: var(--space--8);--dialog__width--max: 1000px;--dialog__width--lrg: 800px;--dialog__width--med: 500px;--dialog__width--sml: 300px;--dialog__bg-color: white;--dialog__padding: 1.5rem;--dialog__height: calc(100vh - 5rem) ;--dialog__scrollbar-width: var(--space--8);--dialog__scrollbar-bg: var(--stlr-color__neutral--150);--dialog__scrollbar-thumb: var(--stlr-color__neutral--400);--dialog__scrollbar-thumb--hover: var(--stlr-color__neutral--500);--dialog__close-bg-color: rgba(255, 255, 255, .8);--dialog__close-padding: var(--space--8);--dialog__close-border-radius: var(--stlr-border-radius);left:var(--dialog__margins);right:var(--dialog__margins);width:100%}@media screen and (min-width:37.5em){  .cdk-global-overlay-wrapper .stlr-dialog-pane.max{max-width:var(--dialog__width--max)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.lrg{max-width:var(--dialog__width--lrg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.med{max-width:var(--dialog__width--med)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.sml{max-width:var(--dialog__width--sml)}}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-dialog-container{width:100%}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-container:empty{display:none}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-backdrop{background-color:#000000bf}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container{position:relative;background:var(--dialog__bg-color);z-index:9999;margin:auto;width:100%;overflow:auto;height:fit-content;max-height:var(--dialog__height);box-sizing:border-box}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container>h4,   .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container .outlet,   .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding:var(--dialog__padding)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container h4{padding-bottom:0}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding-top:0}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar{width:var(--dialog__scrollbar-width)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-track{background-color:var(--dialog__scrollbar-bg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb{background:var(--dialog__scrollbar-thumb)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb:hover{background:var(--dialog__scrollbar-thumb--hover)}@media screen and (min-width:37.5em){  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.max{max-width:var(--dialog__width--max)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.lrg{max-width:var(--dialog__width--lrg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.med{max-width:var(--dialog__width--med)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.sml{max-width:var(--dialog__width--sml)}}  .cdk-global-overlay-wrapper .stlr-dialog-pane .outlet{flex:1 1 auto;overflow-y:auto}  .cdk-global-overlay-wrapper .stlr-dialog-pane .close-icon{appearance:none;border:0;display:flex;position:absolute;right:var(--dialog__margins);top:var(--dialog__margins);background-color:var(--dialog__close-bg-color);border-radius:var(--dialog__close-border-radius);padding:var(--dialog__close-padding);cursor:pointer}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrDialogContainerComponent, [{
    type: Component,
    args: [{
      selector: 'stlr-dialog-container',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: false,
      template: "<div\n  cdkTrapFocus\n  class=\"stlr-dialog-container {{ config.width }}\"\n  [attr.aria-label]=\"config.ariaLabel\"\n  [attr.aria-labelledby]=\"config.ariaLabeledBy\"\n  [attr.aria-describedby]=\"config.ariaDescribedBy\"\n  [attr.role]=\"config.role\"\n  stlrLayout=\"column\"\n  >\n  <button aria-label=\"Close\" class=\"close-icon\" (click)=\"dialogRef.close()\">\n    <stlr-icon size=\"sml\"> close </stlr-icon>\n  </button>\n\n  @if (config.headerText) {\n    <h4 style=\"margin-bottom: var(--space-m);\">{{ config.headerText }}</h4>\n  }\n\n  <div class=\"outlet\">\n    <!-- Portal outlet is used to tell the CDK dialog container we extended where to render content -->\n    <ng-template cdkPortalOutlet></ng-template>\n  </div>\n\n  @if ((config?.actions?.length ?? 0) > 0) {\n    <footer stlrLayout=\"row\" stlrLayoutAlign=\"end center\" stlrLayoutGap=\"var(--space-s)\" style=\"margin-top: var(--space-m);\">\n      @for (action of config.actions; track action) {\n        <button\n          stlrButton\n          (click)=\"action.action(dialogRef)\"\n          [type]=\"action.buttonConfig.type || 'button'\"\n          [btnSize]=\"action.buttonConfig.size\"\n          [btnType]=\"action.buttonConfig.buttonType\"\n          [btnWidth]=\"action.buttonConfig.width\"\n          [icon]=\"action.buttonConfig.icon\"\n          >\n          {{ action.label }}\n        </button>\n      }\n    </footer>\n  }\n</div>\n",
      styles: ["::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane{--dialog__margins: var(--space--8);--dialog__width--max: 1000px;--dialog__width--lrg: 800px;--dialog__width--med: 500px;--dialog__width--sml: 300px;--dialog__bg-color: white;--dialog__padding: 1.5rem;--dialog__height: calc(100vh - 5rem) ;--dialog__scrollbar-width: var(--space--8);--dialog__scrollbar-bg: var(--stlr-color__neutral--150);--dialog__scrollbar-thumb: var(--stlr-color__neutral--400);--dialog__scrollbar-thumb--hover: var(--stlr-color__neutral--500);--dialog__close-bg-color: rgba(255, 255, 255, .8);--dialog__close-padding: var(--space--8);--dialog__close-border-radius: var(--stlr-border-radius);left:var(--dialog__margins);right:var(--dialog__margins);width:100%}@media screen and (min-width:37.5em){::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.max{max-width:var(--dialog__width--max)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.lrg{max-width:var(--dialog__width--lrg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.med{max-width:var(--dialog__width--med)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.sml{max-width:var(--dialog__width--sml)}}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-dialog-container{width:100%}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-container:empty{display:none}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-backdrop{background-color:#000000bf}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container{position:relative;background:var(--dialog__bg-color);z-index:9999;margin:auto;width:100%;overflow:auto;height:fit-content;max-height:var(--dialog__height);box-sizing:border-box}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container>h4,::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container .outlet,::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding:var(--dialog__padding)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container h4{padding-bottom:0}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding-top:0}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar{width:var(--dialog__scrollbar-width)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-track{background-color:var(--dialog__scrollbar-bg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb{background:var(--dialog__scrollbar-thumb)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb:hover{background:var(--dialog__scrollbar-thumb--hover)}@media screen and (min-width:37.5em){::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.max{max-width:var(--dialog__width--max)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.lrg{max-width:var(--dialog__width--lrg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.med{max-width:var(--dialog__width--med)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.sml{max-width:var(--dialog__width--sml)}}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .outlet{flex:1 1 auto;overflow-y:auto}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .close-icon{appearance:none;border:0;display:flex;position:absolute;right:var(--dialog__margins);top:var(--dialog__margins);background-color:var(--dialog__close-bg-color);border-radius:var(--dialog__close-border-radius);padding:var(--dialog__close-padding);cursor:pointer}\n"]
    }]
  }], null, {
    id: [{
      type: HostBinding,
      args: ['id']
    }]
  });
})();

/**
 * Injection token for data being passed into a dialog
 */
const STLR_DIALOG_DATA = new InjectionToken('STLR_DIALOG_DATA');
/**
 * Injection token for providing configuration data to the dialog service
 */
const STLR_DIALOG_DEFAULT_OPTIONS = new InjectionToken('STLR_DIALOG_DEFAULT_OPTIONS');
class StlrDialogService {
  constructor() {
    this._dialog = inject(Dialog);
    this._destroyed$ = new Subject();
    this._openDialogs = [];
  }
  get allOpenDialogs() {
    return this._openDialogs;
  }
  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
  open(componentOrTemplateRef, config) {
    const existingDialog = this._openDialogs.find(d => d.id === config?.id);
    if (existingDialog) {
      throw new Error(`Dialog with ID ${config?.id} is already open.`);
    }
    let dialogRef;
    config = config || new StlrDialogConfig();
    config.id = config.id || `stlr-dialog-${generateUUID(4)}`;
    config.role = config.role || 'dialog';
    config.width = config.width || 'med';
    const cdkDialogRef = this._dialog.open(componentOrTemplateRef, {
      ...config,
      // Component into which the dialog content will be rendered. Pass on our config options.
      container: {
        type: StlrDialogContainerComponent,
        providers: () => [{
          provide: StlrDialogConfig,
          useValue: config
        }, {
          provide: StlrDialogRef,
          useExisting: dialogRef
        }]
      },
      // Context that will be passed to template-based dialogs
      templateContext: () => ({
        dialogRef
      }),
      // Lazily generate the providers that will be exposed to the contents of the dialog
      providers: (_dialogRef, _config, _containerOutlet) => {
        dialogRef = new StlrDialogRef(_dialogRef);
        return [{
          provide: StlrDialogContainerComponent,
          useValue: _containerOutlet
        }, {
          provide: STLR_DIALOG_DATA,
          useValue: _config.data
        }, {
          provide: StlrDialogRef,
          useValue: dialogRef
        }];
      }
    });
    cdkDialogRef.closed.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      const idx = this._openDialogs.findIndex(d => d.id === config?.id);
      if (idx > -1) {
        this._openDialogs.splice(idx, 1);
      }
    });
    const dialog = new StlrDialogRef(cdkDialogRef);
    this._openDialogs.push(dialog);
    return dialog;
  }
  closeAll() {
    this._dialog.closeAll();
  }
  static {
    this.ɵfac = function StlrDialogService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrDialogService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: StlrDialogService,
      factory: StlrDialogService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrDialogService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class StlrDialogCloseDirective {
  constructor() {
    /**
     * Injects the DialogRef hosting this directive
     * @private
     */
    this._dialogRef = inject(StlrDialogRef);
  }
  /**
   * Closes the dialog when an element tagged with this directive is clicked
   */
  onClick() {
    this._dialogRef.close(this.dialogResult);
  }
  static {
    this.ɵfac = function StlrDialogCloseDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrDialogCloseDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: StlrDialogCloseDirective,
      selectors: [["", "stlrDialogClose", ""], ["", "stlr-dialog-close", ""]],
      hostBindings: function StlrDialogCloseDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function StlrDialogCloseDirective_click_HostBindingHandler() {
            return ctx.onClick();
          });
        }
      },
      inputs: {
        dialogResult: [0, "stlr-dialog-close", "dialogResult"]
      },
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrDialogCloseDirective, [{
    type: Directive,
    args: [{
      selector: '[stlrDialogClose], [stlr-dialog-close]',
      standalone: false
    }]
  }], null, {
    dialogResult: [{
      type: Input,
      args: ['stlr-dialog-close']
    }],
    onClick: [{
      type: HostListener,
      args: ['click']
    }]
  });
})();
class StlrDialogModule {
  static {
    this.ɵfac = function StlrDialogModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrDialogModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: StlrDialogModule,
      declarations: [StlrDialogContainerComponent, StlrDialogCloseDirective],
      imports: [CommonModule, DialogModule, OverlayModule, PortalModule, A11yModule, StlrIconModule, StlrButtonModule, StlrFlexLayoutModule],
      exports: [StlrDialogContainerComponent, StlrDialogCloseDirective]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [StlrDialogService, Dialog],
      imports: [CommonModule, DialogModule, OverlayModule, PortalModule, A11yModule, StlrIconModule, StlrButtonModule, StlrFlexLayoutModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrDialogModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, DialogModule, OverlayModule, PortalModule, A11yModule, StlrIconModule, StlrButtonModule, StlrFlexLayoutModule],
      exports: [StlrDialogContainerComponent, StlrDialogCloseDirective],
      declarations: [StlrDialogContainerComponent, StlrDialogCloseDirective],
      providers: [StlrDialogService, Dialog]
    }]
  }], null, null);
})();
class DialogAction {
  constructor(label, action, buttonConfig = new ButtonConfig()) {
    this.label = label;
    this.action = action;
    this.buttonConfig = buttonConfig;
  }
}
class ButtonConfig {
  constructor(config) {
    this.buttonType = 'default';
    this.type = 'button';
    if (config) {
      this.buttonType = config.buttonType;
      this.icon = config.icon;
      this.size = config.size;
      this.type = config.type;
      this.width = config.width;
    }
  }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonConfig, DialogAction, STLR_DIALOG_DATA, STLR_DIALOG_DEFAULT_OPTIONS, StlrDialogCloseDirective, StlrDialogConfig, StlrDialogContainerComponent, StlrDialogModule, StlrDialogRef, StlrDialogService };
